/**
 * Snaps the chat to the left or right side of the screen when the user drags
 * the chat, and drops it towards the left, or towards the right.
 */
export default class ChatPositionSnapping {
  /**
   * The position of the chat before the user started dragging it.
   * This is used so we know if the user dragged from:
   *
   * 1. left to right, the user wants to snap right
   * 2. left to left, the user wants to snap left
   * 3. right to right, the user wants to snap right
   * 4. right to left, the user wants to snap left
   */
  chatPositionBeforeDragStart: DOMRect;

  /**
   * @param chatWrapper The element that wraps the chat, used to calculate the
   * chat's location on the screen. The IFrame element is good for this.
   */
  constructor(private chatWrapper: HTMLElement) {
    this.chatWrapper = chatWrapper;
  }

  /**
   * Snaps the chat to the left or right side based on where the user started
   * dragging the chat from, and where they dropped it.
   *
   * Our requirement is that if the user moved the chat more than 25% of the
   * screen, it should snap to the other side. If the user moved the chat less
   * than 25% of the screen, it should snap back to its original position.
   */
  snapChatPosition() {
    const startPosition = this.getChatOrientationBeforeDrag();
    const movedPosition = this.getChatPositionHorizontalPercentage();

    const leftToLeft = startPosition === "left" && movedPosition <= 25;
    const leftToRight = startPosition === "left" && movedPosition > 25;
    const rightToRight = startPosition === "right" && movedPosition >= 75;
    const rightToLeft = startPosition === "right" && movedPosition < 75;

    if (leftToRight || rightToRight) {
      this.snapRight();
    } else if (leftToLeft || rightToLeft) {
      this.snapLeft();
    }

    this.saveWindowPosition();
  }

  onDragStart() {
    this.chatPositionBeforeDragStart = this.chatWrapper.getBoundingClientRect();
  }

  /**
   * @returns A number between 0 and 100 representing the horizontal position of
   * the chat on the screen
   *
   * @example
   * 0 = left edge of screen
   * 50 = middle of screen
   * 100 = right edge of screen
   */
  private getChatPositionHorizontalPercentage() {
    const chatPosition = this.chatWrapper.getBoundingClientRect();
    const chatCenterX = chatPosition.left + chatPosition.width / 2;
    const screenWidth = window.parent.visualViewport.width;
    const chatHorizontalPositionPercentage = Math.round(
      (chatCenterX / screenWidth) * 100
    );

    return chatHorizontalPositionPercentage;
  }

  /**
   * Moves the chat to the left side of the screen.
   * Takes into account the portal configuration for the skin's X margin.
   */
  private snapLeft() {
    const skinLeft: number =
      window.messagingApp.messageWindow.theme?.skinLeft ?? 0;

    window.messagingApp.messageWindow.moveStage(skinLeft);
  }

  /**
   * Moves the chat to the right side of the screen.
   * Takes into account the portal configuration for the skin's X margin.
   */
  private snapRight() {
    const chatWidth: number = window.messagingApp.messageWindow.area.width;
    const screenWidth = window.parent.visualViewport.width;
    const skinLeft: number =
      window.messagingApp.messageWindow.theme?.skinLeft ?? 0;

    window.messagingApp.messageWindow.moveStage(
      screenWidth - chatWidth - skinLeft
    );
  }

  /**
   * Get chat position before the user started dragging it.
   *
   * @returns "left" if the chat was on the left half of the screen
   *          "right" if the chat was on the right half of the screen.
   */
  private getChatOrientationBeforeDrag(): "left" | "right" {
    const centerOfChatBeforeDrag =
      this.chatPositionBeforeDragStart.left +
      this.chatPositionBeforeDragStart.width / 2;
    const visualViewportWidth = window.parent.visualViewport.width;

    if (centerOfChatBeforeDrag < visualViewportWidth / 2) {
      return "left";
    } else {
      return "right";
    }
  }

  private saveWindowPosition() {
    const chatArea = window.messagingApp.messageWindow.area;
    const skinBottom: number = window.messagingApp.messageWindow.theme?.skinTop;

    window.messagingApp
      .getSDKInstance()
      .saveWindowPosition(skinBottom, chatArea.r);
  }
}
